import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiBookOpen,
  FiRss,
  FiHome,
  FiUsers,
  FiBriefcase,
  FiUser,
  FiChevronLeft,
  FiMessageCircle,
  FiBell,
  FiSettings,
  FiGrid,
  FiAward,
  FiBook,
  FiLock,
  FiUnlock,
  FiLogIn,
  FiUserPlus,
  FiX,
  FiChevronRight,
  FiMoreHorizontal,
} from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";
import { useNotificationContext } from "../../context/NotificationContext";
import "./SidebarNavbar.css";
import NotificationsOverlay from "../notifications/NotificationsOverlay";
import { getProfilePhoto, getCachedPhoto, updatePhotoCache } from "../../utils/profilePhotoCache";
import { getAuthToken } from "../../utils/apiUtils";

// ==============================================
// Navigation Configuration
// ==============================================
const NAV_ITEMS = [
  {
    id: "explore",
    name: "Explore Papers",
    path: "/explore",
    icon: FiBookOpen,
    color: "#3b82f6",
    bg: "#dbeafe",
  },
  {
    id: "feed",
    name: "Research Feed",
    path: "/feed-home",
    icon: FiRss,
    color: "#8b5cf6",
    bg: "#ede9fe",
  },
  {
    id: "labs",
    name: "Research Labs",
    path: "/labs",
    icon: FiHome,
    color: "#10b981",
    bg: "#d1fae5",
  },
  {
    id: "communities",
    name: "Communities",
    path: "/communities",
    icon: FiGrid,
    color: "#f59e0b",
    bg: "#fef3c7",
    isNew: true,
  },
  {
    id: "workshops",
    name: "Workshops",
    path: "/workshops",
    icon: FiAward,
    color: "#ec4899",
    bg: "#fce7f3",
    isNew: true,
  },
  {
    id: "collaborators",
    name: "Collaborators",
    path: "/collaborators",
    icon: FiUsers,
    color: "#06b6d4",
    bg: "#cffafe",
  },
  {
    id: "freelance",
    name: "Freelance",
    path: "/freelance",
    icon: FiBriefcase,
    color: "#6366f1",
    bg: "#e0e7ff",
  },
  {
    id: "courses",
    name: "Courses",
    path: "/courses",
    icon: FiBook,
    color: "#14b8a6",
    bg: "#ccfbf1",
    isNew: true,
  },
];

const UTILITY_ITEMS = [
  {
    id: "notifications",
    name: "Notifications",
    icon: FiBell,
    color: "#ef4444",
    bg: "#fee2e2",
    isNotification: true,
    showBadge: true, // NEW: Badge config
    badgeType: 'notification' // NEW: Badge type
  },
  {
    id: "messages",
    name: "Messages",
    path: "/messages",
    icon: FiMessageCircle,
    color: "#8b5cf6",
    bg: "#ede9fe",
    showBadge: true, // NEW: Badge config
    badgeType: 'messaging' // NEW: Badge type for messaging
  },
  {
    id: "settings",
    name: "Settings",
    path: "/settings",
    icon: FiSettings,
    color: "#64748b",
    bg: "#f1f5f9",
  },
];

// Mobile bottom navigation items (7 items: 3 left + Feed center + 3 right)
const MOBILE_NAV_ITEMS = [
  // LEFT 3
  {
    id: "explore",
    name: "Explore",
    path: "/explore",
    icon: FiBookOpen,
    color: "#3b82f6",
  },
  {
    id: "communities",
    name: "Communities",
    path: "/communities",
    icon: FiGrid,
    color: "#f59e0b",
  },
  {
    id: "labs",
    name: "Labs",
    path: "/labs",
    icon: FiHome,
    color: "#10b981",
  },
  // CENTER
  {
    id: "feed",
    name: "Feed",
    path: "/feed-home",
    icon: FiRss,
    color: "#8b5cf6",
    isCenter: true,
  },
  // RIGHT 3
  {
    id: "workshops",
    name: "Workshops",
    path: "/workshops",
    icon: FiAward,
    color: "#ec4899",
  },
  {
    id: "collaborators",
    name: "Collaborators",
    path: "/collaborators",
    icon: FiUsers,
    color: "#06b6d4",
  },
  {
    id: "more",
    name: "More",
    icon: FiMoreHorizontal,
    color: "#64748b",
    isMore: true,
  },
];

// More menu items (shown when tapping More button)
const MORE_MENU_ITEMS = [
  {
    id: "profile",
    name: "My Profile",
    path: "/profile",
    icon: FiUser,
    color: "#8b5cf6",
    bg: "#ede9fe",
  },
  {
    id: "freelance",
    name: "Freelance",
    path: "/freelance",
    icon: FiBriefcase,
    color: "#6366f1",
    bg: "#e0e7ff",
  },
  {
    id: "courses",
    name: "Courses",
    path: "/courses",
    icon: FiBook,
    color: "#14b8a6",
    bg: "#ccfbf1",
    isNew: true,
  },
  {
    id: "settings",
    name: "Settings",
    path: "/settings",
    icon: FiSettings,
    color: "#64748b",
    bg: "#f1f5f9",
  },
];

// ==============================================
// Animation Variants
// ==============================================
const sidebarVariants = {
  expanded: { width: 256 },
  collapsed: { width: 72 },
};

const profileMenuVariants = {
  hidden: { y: "100%", opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", damping: 25, stiffness: 300 },
  },
  exit: { y: "100%", opacity: 0, transition: { duration: 0.2 } },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

// ==============================================
// Utility Functions
// ==============================================
const getUserInitials = (user) => {
  if (!user) return "U";
  const name = user.name || user.username || user.email || "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0]?.substring(0, 2).toUpperCase() || "U";
};

// ==============================================
// Sub-Components
// ==============================================

// Logo Component
const Logo = ({ isCollapsed }) => (
  <Link to="/" className="snav-logo">
    <AnimatePresence>
      {!isCollapsed ? (
        <motion.span
          className="snav-logo-text"
          initial={{ opacity: 0, width: 0 }}
          animate={{ opacity: 1, width: "auto" }}
          exit={{ opacity: 0, width: 0 }}
        >
          research pro
        </motion.span>
      ) : (
        <motion.span
          className="snav-logo-text snav-logo-text--collapsed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          rp
        </motion.span>
      )}
    </AnimatePresence>
  </Link>
);

// Navigation Item Component (Desktop)
const NavItem = ({
  item,
  isActive,
  isCollapsed,
  isLocked,
  unreadCount,
  messagingUnreadCount, // NEW: Messaging unread count
  onNotificationClick,
}) => {
  const Icon = item.icon;
  const isNotification = item.isNotification;

  // NEW: Determine which count to show based on badge type
  const badgeCount = item.badgeType === 'messaging' 
    ? messagingUnreadCount 
    : unreadCount;

  const handleClick = (e) => {
    if (isNotification) {
      e.preventDefault();
      onNotificationClick?.();
    } else if (isLocked) {
      e.preventDefault();
    }
  };

  const content = (
    <motion.div
      className={`snav-item ${isActive ? "snav-item--active" : ""} ${
        isLocked ? "snav-item--locked" : ""
      }`}
      style={{
        "--item-color": item.color,
        "--item-bg": item.bg,
      }}
      whileHover={{ x: isCollapsed ? 0 : 4, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
    >
      <div className="snav-item-icon">
        <Icon />
        {/* NEW: Show badge for both notification and messaging */}
        {item.showBadge && badgeCount > 0 && (
          <span className="snav-notification-badge">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </div>

      <AnimatePresence>
        {!isCollapsed && (
          <motion.span
            className="snav-item-text"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
          >
            {item.name}
          </motion.span>
        )}
      </AnimatePresence>

      {!isCollapsed && item.isNew && (
        <span className="snav-new-badge">NEW</span>
      )}

      {!isCollapsed && isLocked && <FiLock className="snav-lock-icon" />}
    </motion.div>
  );

  if (isNotification || isLocked) {
    return (
      <div className="snav-item-wrapper" data-tooltip={item.name}>
        {content}
      </div>
    );
  }

  return (
    <Link to={item.path} className="snav-item-wrapper" data-tooltip={item.name}>
      {content}
    </Link>
  );
};

// User Profile Component (Desktop)
const UserProfile = ({
  user,
  isCollapsed,
  isAuthenticated,
  cachedPhoto,
  onNavigate,
}) => {
  const photoUrl = cachedPhoto || user?.profilePhotoUrl || user?.avatar;

  return (
    <motion.div
      className="snav-profile"
      whileHover={{ scale: 1.02 }}
      onClick={() => onNavigate(isAuthenticated ? "/profile" : "/signin")}
    >
      <div className="snav-profile-avatar">
        {isAuthenticated && photoUrl ? (
          <img src={photoUrl} alt={user?.name || "User"} />
        ) : isAuthenticated ? (
          <span className="snav-profile-initials">{getUserInitials(user)}</span>
        ) : (
          <FiUser />
        )}
        <div className="snav-profile-status" />
      </div>

      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            className="snav-profile-info"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
          >
            <span className="snav-profile-name">
              {isAuthenticated ? user?.name || "User" : "Guest"}
            </span>
            <span className="snav-profile-role">
              {isAuthenticated
                ? user?.professionalRole || user?.role || "Member"
                : "Sign in to continue"}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Auth Banner Component (Desktop)
const AuthBanner = ({ isCollapsed }) => {
  if (isCollapsed) return null;

  return (
    <motion.div
      className="snav-auth-banner"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="snav-auth-icon">
        <FiUnlock />
      </div>
      <div className="snav-auth-content">
        <span className="snav-auth-title">Unlock Features</span>
        <div className="snav-auth-buttons">
          <Link to="/signin" className="snav-auth-btn snav-auth-btn--signin">
            <FiLogIn /> Sign In
          </Link>
          <Link to="/signup" className="snav-auth-btn snav-auth-btn--signup">
            <FiUserPlus /> Sign Up
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// ==============================================
// Mobile Components
// ==============================================

// Mobile Header Component (Fixed at top)
const MobileHeader = ({
  user,
  isAuthenticated,
  cachedPhoto,
  unreadCount,
  messagingUnreadCount,
  onProfileClick,
  onNotificationToggle,
  onMessagesClick,
}) => {
  const photoUrl = cachedPhoto || user?.profilePhotoUrl || user?.avatar;

  return (
    <header className="snav-mobile-header">
      {/* Left: Profile Avatar */}
      <div className="snav-mh-left">
        <motion.div
          className="snav-mh-avatar"
          onClick={onProfileClick}
          whileTap={{ scale: 0.95 }}
        >
          {isAuthenticated && photoUrl ? (
            <img src={photoUrl} alt={user?.name || "User"} />
          ) : isAuthenticated ? (
            <span>{getUserInitials(user)}</span>
          ) : (
            <FiUser />
          )}
        </motion.div>

        {/* App Logo & Name */}
        <Link to="/" className="snav-mh-brand">
          <span className="snav-mh-name">research pro</span>
        </Link>
      </div>

      {/* Right: Notifications & Messages */}
      <div className="snav-mh-right">
        <motion.button
          className="snav-mh-btn"
          onClick={onNotificationToggle}
          whileTap={{ scale: 0.95 }}
          aria-label="Notifications"
        >
          <FiBell />
          {unreadCount > 0 && (
            <span className="snav-mh-badge">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </motion.button>

        <motion.button
          className="snav-mh-btn"
          onClick={onMessagesClick}
          whileTap={{ scale: 0.95 }}
          aria-label="Messages"
        >
          <FiMessageCircle />
          {messagingUnreadCount > 0 && (
            <span className="snav-mh-badge">
              {messagingUnreadCount > 99 ? "99+" : messagingUnreadCount}
            </span>
          )}
        </motion.button>
      </div>
    </header>
  );
};

// Mobile Bottom Navigation Component (Fixed at bottom - 7 items)
const MobileBottomNav = ({
  navItems,
  currentPath,
  isAuthenticated,
  onNavigate,
  onMoreClick,
}) => {
  const isActive = (path) => {
    // Feed routes: /feed-home and /posts/* are all feed
    if (path === "/feed-home") {
      return currentPath === "/" || 
             currentPath === "/feed-home" || 
             currentPath.startsWith("/posts/");
    }
    if (path === "/") return currentPath === "/";
    return currentPath.startsWith(path);
  };

  return (
    <nav className="snav-bottom-nav snav-bottom-nav--7items">
      {navItems.map((item) => {
        const Icon = item.icon;
        const active = item.path ? isActive(item.path) : false;
        const isLocked = !isAuthenticated && item.path && item.path !== "/explore";

        // Handle More button separately
        if (item.isMore) {
          return (
            <motion.button
              key={item.id}
              className="snav-bn-item snav-bn-item--more"
              style={{ "--item-color": item.color }}
              onClick={onMoreClick}
              whileTap={{ scale: 0.9 }}
              aria-label={item.name}
            >
              <div className="snav-bn-icon">
                <Icon />
              </div>
              <span className="snav-bn-label">{item.name}</span>
            </motion.button>
          );
        }

        return (
          <motion.button
            key={item.id}
            className={`snav-bn-item ${active ? "snav-bn-item--active" : ""} ${
              item.isCenter ? "snav-bn-item--center" : ""
            } ${isLocked ? "snav-bn-item--locked" : ""}`}
            style={{ "--item-color": item.color }}
            onClick={() => !isLocked && onNavigate(item.path)}
            whileTap={{ scale: 0.9 }}
            aria-label={item.name}
            aria-current={active ? "page" : undefined}
          >
            <div className="snav-bn-icon">
              <Icon />
              {isLocked && <FiLock className="snav-bn-lock" />}
            </div>
            <span className="snav-bn-label">{item.name}</span>
            {active && (
              <motion.div
                className="snav-bn-indicator"
                layoutId="bottomNavIndicator"
              />
            )}
          </motion.button>
        );
      })}
    </nav>
  );
};

// More Menu Slide-Up Sheet (for Profile, Freelance, Courses, Settings)
const ProfileMenu = ({
  isOpen,
  onClose,
  user,
  isAuthenticated,
  cachedPhoto,
  menuItems,
  onNavigate,
}) => {
  const photoUrl = cachedPhoto || user?.profilePhotoUrl || user?.avatar;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className="snav-pm-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          {/* Menu Sheet */}
          <motion.div
            className="snav-pm-sheet"
            variants={profileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Handle bar */}
            <div className="snav-pm-handle" />

            {/* Header with profile info */}
            <div className="snav-pm-header">
              <div className="snav-pm-profile">
                <div className="snav-pm-avatar">
                  {isAuthenticated && photoUrl ? (
                    <img src={photoUrl} alt={user?.name || "User"} />
                  ) : isAuthenticated ? (
                    <span>{getUserInitials(user)}</span>
                  ) : (
                    <FiUser />
                  )}
                </div>
                <div className="snav-pm-info">
                  <span className="snav-pm-name">
                    {isAuthenticated ? user?.name || "User" : "Guest"}
                  </span>
                  <span className="snav-pm-role">
                    {isAuthenticated
                      ? user?.professionalRole || "Member"
                      : "Sign in to continue"}
                  </span>
                </div>
              </div>
              <motion.button
                className="snav-pm-close"
                onClick={onClose}
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX />
              </motion.button>
            </div>

            {/* Menu Items */}
            <div className="snav-pm-items">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isLocked = !isAuthenticated && item.id !== "profile";

                return (
                  <motion.div
                    key={item.id}
                    className={`snav-pm-item ${
                      isLocked ? "snav-pm-item--locked" : ""
                    }`}
                    style={{ "--item-color": item.color, "--item-bg": item.bg }}
                    onClick={() => {
                      if (!isLocked) {
                        onNavigate(item.path);
                        onClose();
                      }
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="snav-pm-item-icon">
                      <Icon />
                    </div>
                    <span className="snav-pm-item-text">{item.name}</span>
                    {item.isNew && <span className="snav-pm-new">NEW</span>}
                    {isLocked ? (
                      <FiLock className="snav-pm-item-arrow" />
                    ) : (
                      <FiChevronRight className="snav-pm-item-arrow" />
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Auth buttons for guests */}
            {!isAuthenticated && (
              <div className="snav-pm-auth">
                <Link
                  to="/signin"
                  className="snav-pm-auth-btn snav-pm-auth-btn--signin"
                  onClick={onClose}
                >
                  <FiLogIn /> Sign In
                </Link>
                <Link
                  to="/signup"
                  className="snav-pm-auth-btn snav-pm-auth-btn--signup"
                  onClick={onClose}
                >
                  <FiUserPlus /> Create Account
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ==============================================
// Main Sidebar Component
// ==============================================
const SidebarNavbar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [cachedProfilePhoto, setCachedProfilePhoto] = useState(
    getCachedPhoto()
  );

  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUserContext();
  const { 
    unreadCount, 
    messagingUnreadCount, // NEW: Messaging unread count
    refreshUnreadCount 
  } = useNotificationContext();

  // Load profile photo
  useEffect(() => {
    const loadPhoto = async () => {
      const token = getAuthToken();
      if (token && isAuthenticated && user) {
        // If user context has a profilePhotoUrl, use it as the authoritative source
        // This prevents stale localStorage cache (e.g. old S3 bucket URL) from being shown
        if (user.profilePhotoUrl) {
          const cached = getCachedPhoto();
          if (cached !== user.profilePhotoUrl) {
            updatePhotoCache(user.profilePhotoUrl, user.photoUpdatedAt || '');
          }
          setCachedProfilePhoto(user.profilePhotoUrl);
          return;
        }

        // No URL in context — fall back to async cache fetch
        const photo = await getProfilePhoto(token, user.photoUpdatedAt);
        setCachedProfilePhoto(photo);
      } else {
        setCachedProfilePhoto(null);
      }
    };
    loadPhoto();
  }, [isAuthenticated, user?.profilePhotoUrl, user?.photoUpdatedAt, user]);

  // Refresh notifications
  useEffect(() => {
    if (isAuthenticated) {
      refreshUnreadCount();
      const interval = setInterval(refreshUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, refreshUnreadCount]);

  // Handlers
  const handleNotificationToggle = useCallback(() => {
    if (isAuthenticated) {
      setShowNotifications((prev) => !prev);
    }
  }, [isAuthenticated]);

  const handleNavigate = useCallback(
    (path) => {
      navigate(path);
    },
    [navigate]
  );

  const handleMoreMenuToggle = useCallback(() => {
    setShowMoreMenu((prev) => !prev);
  }, []);

  const isActiveItem = useCallback(
    (path) => {
      // Feed routes: /, /feed-home, /posts/* are all feed
      if (path === "/feed-home") {
        return location.pathname === "/" || 
               location.pathname === "/feed-home" || 
               location.pathname.startsWith("/posts/");
      }
      if (path === "/") return location.pathname === "/";
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  // Handle click on collapsed sidebar to expand
  const handleSidebarClick = (e) => {
    if (
      isCollapsed &&
      !e.target.closest(".snav-item-wrapper") &&
      !e.target.closest(".snav-profile") &&
      !e.target.closest(".snav-auth-btn")
    ) {
      setIsCollapsed(false);
    }
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <motion.aside
        className={`snav-sidebar ${
          isCollapsed ? "snav-sidebar--collapsed" : ""
        }`}
        variants={sidebarVariants}
        initial={false}
        animate={isCollapsed ? "collapsed" : "expanded"}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={handleSidebarClick}
      >
        {/* Header */}
        <div className="snav-header">
          <Logo isCollapsed={isCollapsed} />
          
          {/* Red dot if any unread notifications or messages */}
          {isAuthenticated && (unreadCount > 0 || messagingUnreadCount > 0) && (
            <span className="snav-header-dot" />
          )}

          {!isCollapsed && (
            <motion.button
              className="snav-toggle"
              onClick={() => setIsCollapsed(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiChevronLeft />
            </motion.button>
          )}
        </div>

        {/* Auth Banner for guests */}
        {!isAuthenticated && <AuthBanner isCollapsed={isCollapsed} />}

        {/* Main Navigation */}
        <nav className="snav-nav">
          <div className="snav-section">
            {!isCollapsed && <span className="snav-section-title">Main</span>}
            {NAV_ITEMS.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                isActive={isActiveItem(item.path)}
                isCollapsed={isCollapsed}
                isLocked={!isAuthenticated && item.path !== "/explore"}
              />
            ))}
          </div>

          <div className="snav-divider" />

          <div className="snav-section">
            {!isCollapsed && (
              <span className="snav-section-title">Utilities</span>
            )}
            {UTILITY_ITEMS.map((item) => (
              <NavItem
                key={item.id}
                item={item}
                isActive={item.path && isActiveItem(item.path)}
                isCollapsed={isCollapsed}
                isLocked={!isAuthenticated && !item.isNotification}
                unreadCount={item.isNotification ? unreadCount : 0}
                messagingUnreadCount={messagingUnreadCount} // NEW: Pass messaging unread count
                onNotificationClick={handleNotificationToggle}
              />
            ))}
          </div>
        </nav>

        {/* Profile Section */}
        <UserProfile
          user={user}
          isCollapsed={isCollapsed}
          isAuthenticated={isAuthenticated}
          cachedPhoto={cachedProfilePhoto}
          onNavigate={handleNavigate}
        />
      </motion.aside>

      {/* Mobile Header */}
      <MobileHeader
        user={user}
        isAuthenticated={isAuthenticated}
        cachedPhoto={cachedProfilePhoto}
        unreadCount={unreadCount}
        messagingUnreadCount={messagingUnreadCount}
        onProfileClick={() =>
          handleNavigate(isAuthenticated ? "/profile" : "/signin")
        }
        onNotificationToggle={handleNotificationToggle}
        onMessagesClick={() => handleNavigate("/messages")}
      />

      {/* Mobile Bottom Navigation (7 items) */}
      <MobileBottomNav
        navItems={MOBILE_NAV_ITEMS}
        currentPath={location.pathname}
        isAuthenticated={isAuthenticated}
        onNavigate={handleNavigate}
        onMoreClick={handleMoreMenuToggle}
      />

      {/* More Menu Slide-Up Sheet */}
      <ProfileMenu
        isOpen={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
        user={user}
        isAuthenticated={isAuthenticated}
        cachedPhoto={cachedProfilePhoto}
        menuItems={MORE_MENU_ITEMS}
        onNavigate={handleNavigate}
      />

      {/* Notifications Overlay */}
      {isAuthenticated && (
        <NotificationsOverlay
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
        />
      )}
    </>
  );
};

export default SidebarNavbar;
